/**
 * Key Derivation Functions (KDF) and Hashing
 */
import { decodeBase64, encodeBase64 } from './codec.js';
/**
 * Generate a random salt for PBKDF2
 * @returns Base64 encoded salt
 */
export function generateSalt() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return encodeBase64(array);
}
/**
 * Derive an encryption key from a phrase and salt using PBKDF2
 * Spec: 200,000 iterations, SHA-256
 */
export async function deriveKey(phrase, salt) {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(phrase), { name: 'PBKDF2' }, false, ['deriveBits', 'deriveKey']);
    const saltBuffer = decodeBase64(salt);
    return await crypto.subtle.deriveKey({
        name: 'PBKDF2',
        salt: saltBuffer,
        iterations: 200000,
        hash: 'SHA-256',
    }, keyMaterial, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}
/**
 * Export a key as a raw SHA-256 hash (used for identity ID)
 * key_hash = SHA-256(key)
 */
export async function hashKey(key) {
    const rawKey = await crypto.subtle.exportKey('raw', key);
    const hashBuffer = await crypto.subtle.digest('SHA-256', rawKey);
    return encodeBase64(hashBuffer);
}
/**
 * Generate a proof for the login challenge
 * proof = SHA-256(challenge || key_hash)
 */
export async function generateProof(challenge, keyHash) {
    const enc = new TextEncoder();
    const data = challenge + keyHash;
    const buffer = await crypto.subtle.digest('SHA-256', enc.encode(data));
    return encodeBase64(buffer);
}
/**
 * Validates 32-byte hex key and imports as CryptoKey
 */
export async function getEncryptionKey(hexKey) {
    const match = hexKey.match(/.{1,2}/g);
    if (!match)
        throw new Error('Invalid key format');
    const bytes = new Uint8Array(match.map((byte) => parseInt(byte, 16)));
    return crypto.subtle.importKey('raw', bytes, { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt',
    ]);
}
//# sourceMappingURL=kdf.js.map